// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { useSafeImageSrc } from '../hooks/useSafeImageSrc';
import {
  Form,
  Header,
  Message,
  TextArea,
  Segment,
  Label,
  Button,
  Icon,
  Grid,
  Dropdown,
  Input,
  Divider
} from 'semantic-ui-react';

type BrandingTier = 'NONE' | 'GREY_LABEL' | 'WHITE_LABEL';

interface BrandingSectionEnhancedProps {
  brandingTier?: BrandingTier;
  logoUrl?: string;
  branding?: Record<string, unknown>;
  onChange: (field: string, value: unknown) => void;
  defaultBranding?: Record<string, unknown>;
}

interface BrandingColors {
  primary?: string;
  secondary?: string;
  accent?: string;
  text?: string;
  background?: string;
}

function BrandingSectionEnhanced({
  brandingTier = 'NONE',
  logoUrl = '',
  branding = {},
  onChange,
  defaultBranding
}: Readonly<BrandingSectionEnhancedProps>) {
  const [brandingJson, setBrandingJson] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showDefaultBranding, setShowDefaultBranding] = useState(false);
  const [useColorPicker, setUseColorPicker] = useState(true);
  const [colors, setColors] = useState<BrandingColors>({
    primary: '#2185d0',
    secondary: '#21ba45',
    accent: '#f2711c',
    text: '#333333',
    background: '#ffffff'
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  // Safe, sanitised src for the logo preview (blob URL for data: incl. SVG;
  // validated http(s) pass-through; else ''). The logo URL is admin-entered.
  const logoPreviewSrc = useSafeImageSrc(logoPreview || logoUrl);

  const brandingTierOptions = [
    {
      key: 'NONE',
      text: 'None (Platform Branding)',
      value: 'NONE',
      description: 'Finerty logo only, default colors - Free tier'
    },
    {
      key: 'GREY_LABEL',
      text: 'Grey Label (Co-Branding)',
      value: 'GREY_LABEL',
      description: 'Brokerage logo + "Powered by Finerty" badge - Standard tier'
    },
    {
      key: 'WHITE_LABEL',
      text: 'White Label (Complete White Label)',
      value: 'WHITE_LABEL',
      description: 'Brokerage logo only, full customization - Premium tier'
    }
  ];

  useEffect(() => {
    // Initialize colors from branding object
    if (branding && typeof branding === 'object' && 'colors' in branding) {
      const brandingColors = branding.colors as BrandingColors;
      setColors({
        primary: brandingColors.primary || '#2185d0',
        secondary: brandingColors.secondary || '#21ba45',
        accent: brandingColors.accent || '#f2711c',
        text: brandingColors.text || '#333333',
        background: brandingColors.background || '#ffffff'
      });
    }

    // Initialize brandingJson from branding object
    if (branding && Object.keys(branding).length > 0) {
      setBrandingJson(JSON.stringify(branding, null, 2));
    } else if (defaultBranding) {
      // Show a sample structure based on default branding
      const sample = {
        colors: {
          primary: '#2185d0',
          secondary: '#21ba45',
          accent: '#f2711c',
          text: '#333333',
          background: '#ffffff'
        },
        typography: {
          fontFamily: 'Lato, sans-serif',
          fontSize: '14px'
        },
        email: {
          headerBackgroundColor: '#2185d0',
          buttonBackgroundColor: '#462872',
          footerTextColor: '#666666'
        }
      };
      setBrandingJson(JSON.stringify(sample, null, 2));
    }

    // Initialize logo preview
    if (logoUrl) {
      setLogoPreview(logoUrl);
    }
  }, [branding, defaultBranding, logoUrl]);

  const handleBrandingTierChange = (value: 'NONE' | 'GREY_LABEL' | 'WHITE_LABEL') => {
    onChange('brandingTier', value);
  };

  const handleColorChange = (colorKey: keyof BrandingColors, value: string) => {
    const updatedColors = { ...colors, [colorKey]: value };
    setColors(updatedColors);

    // Update branding object
    const currentBranding = branding || {};
    const updatedBranding = {
      ...currentBranding,
      colors: updatedColors
    };
    onChange('branding', updatedBranding);
    setBrandingJson(JSON.stringify(updatedBranding, null, 2));
  };

  const handleBrandingChange = (value: string) => {
    setBrandingJson(value);
    setJsonError(null);

    if (!value.trim()) {
      onChange('branding', {});
      return;
    }

    try {
      const parsed = JSON.parse(value);
      onChange('branding', parsed);

      // Update color pickers if colors are in JSON
      if (parsed.colors) {
        setColors(parsed.colors);
      }
    } catch (error) {
      console.debug('JSON parse error:', error);
      setJsonError('Invalid JSON format');
    }
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setJsonError('Please select an image file');
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setJsonError('File size must be less than 2MB');
        return;
      }

      setLogoFile(file);
      setJsonError(null);

      // Create preview and automatically update logoUrl with base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setLogoPreview(base64String);
        // Automatically update logoUrl when file is selected
        onChange('logoUrl', base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLoadDefault = () => {
    if (defaultBranding) {
      const formatted = JSON.stringify(defaultBranding, null, 2);
      setBrandingJson(formatted);
      onChange('branding', defaultBranding);
      setJsonError(null);
    }
  };

  const handleClearBranding = () => {
    setBrandingJson('{}');
    onChange('branding', {});
    setColors({
      primary: '#2185d0',
      secondary: '#21ba45',
      accent: '#f2711c',
      text: '#333333',
      background: '#ffffff'
    });
    setJsonError(null);
  };

  return (
    <>
      <Header as='h3' dividing style={{ marginTop: '30px' }}>
        <Icon name='paint brush' />
        <Header.Content>
          Branding & White-Labeling
          <Header.Subheader>
            Customize the look and feel for this brokerage
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Form.Field>
        <label htmlFor="branding-tier">Branding Tier</label>
        <Dropdown
          id="branding-tier"
          selection
          fluid
          options={brandingTierOptions}
          value={brandingTier}
          onChange={(_, { value }) => handleBrandingTierChange(value as 'NONE' | 'GREY_LABEL' | 'WHITE_LABEL')}
        />
        <Label pointing>
          {brandingTier === 'NONE' && (
            <>
              <Icon name='circle' />
              <strong>None (Free):</strong> Platform branding only - Finerty logo and default colors
            </>
          )}
          {brandingTier === 'GREY_LABEL' && (
            <>
              <Icon name='circle' color='grey' />
              <strong>Grey Label (Standard):</strong> Brokerage logo with "Powered by Finerty" badge
            </>
          )}
          {brandingTier === 'WHITE_LABEL' && (
            <>
              <Icon name='circle' color='blue' />
              <strong>White Label (Premium):</strong> Complete white label with full customization
            </>
          )}
        </Label>
      </Form.Field>

      {(brandingTier === 'GREY_LABEL' || brandingTier === 'WHITE_LABEL') && (
        <>
          <Header as='h4' style={{ marginTop: '20px' }}>
            Logo Upload
            <Header.Subheader>Upload your brokerage logo</Header.Subheader>
          </Header>

          <Form.Field>
            <label htmlFor="branding-logo-file">Logo File (PNG, JPG, or SVG - max 2MB)</label>
            <Input
              id="branding-logo-file"
              type='file'
              accept='image/*'
              onChange={handleLogoFileChange}
            />
            {logoFile && (
              <Label color='green' style={{ marginTop: '10px' }}>
                <Icon name='check circle' />
                Image selected and ready to save
              </Label>
            )}
          </Form.Field>

          <Form.Field>
            <label htmlFor="branding-logo-url">Or enter Logo URL (SVG preferred)</label>
            <input
              id="branding-logo-url"
              type='url'
              name='logoUrl'
              value={logoUrl}
              onChange={(e) => onChange('logoUrl', e.target.value)}
              placeholder='https://example.com/logo.svg'
            />
            <Label pointing>
              Enter a publicly accessible URL to your brokerage logo (SVG format recommended)
            </Label>
          </Form.Field>

          {logoPreviewSrc && (
            <Segment placeholder>
              <Header icon>
                <Icon name='image' />
                Logo Preview
              </Header>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <img
                  src={logoPreviewSrc}
                  alt='Brokerage Logo'
                  style={{ maxWidth: '300px', maxHeight: '100px' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            </Segment>
          )}

          <Divider />
        </>
      )}

      <Header as='h4' style={{ marginTop: '20px' }}>
        Brand Colors
        <Header.Subheader>
          {brandingTier === 'NONE' && 'No color customization available on free tier'}
          {brandingTier === 'GREY_LABEL' && 'Customize primary and secondary colors'}
          {brandingTier === 'WHITE_LABEL' && 'Define your complete brand color palette'}
        </Header.Subheader>
      </Header>

      {brandingTier === 'NONE' && (
        <Message info>
          <Message.Header>Free Tier Restrictions</Message.Header>
          <p>
            The NONE tier uses platform branding only. Upgrade to Grey Label or White Label
            for color customization and logo branding.
          </p>
        </Message>
      )}

      {brandingTier !== 'NONE' && (
      <>
      <Button.Group size='mini' style={{ marginBottom: '15px' }}>
        <Button
          active={useColorPicker}
          onClick={() => setUseColorPicker(true)}
        >
          <Icon name='eye dropper' />
          Color Pickers
        </Button>
        <Button
          active={!useColorPicker}
          onClick={() => setUseColorPicker(false)}
        >
          <Icon name='code' />
          JSON Editor
        </Button>
      </Button.Group>

      {useColorPicker ? (
        <Grid stackable columns={2}>
          <Grid.Row>
            <Grid.Column>
              <Form.Field>
                <label htmlFor="branding-color-primary">Primary Color</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    id="branding-color-primary"
                    type='color'
                    value={colors.primary}
                    onChange={(e) => handleColorChange('primary', e.target.value)}
                    style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                  />
                  <Input
                    fluid
                    value={colors.primary}
                    onChange={(e) => handleColorChange('primary', e.target.value)}
                    placeholder='#2185d0'
                  />
                </div>
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label htmlFor="branding-color-secondary">Secondary Color</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    id="branding-color-secondary"
                    type='color'
                    value={colors.secondary}
                    onChange={(e) => handleColorChange('secondary', e.target.value)}
                    style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                  />
                  <Input
                    fluid
                    value={colors.secondary}
                    onChange={(e) => handleColorChange('secondary', e.target.value)}
                    placeholder='#21ba45'
                  />
                </div>
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Form.Field>
                <label htmlFor="branding-color-accent">Accent Color</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    id="branding-color-accent"
                    type='color'
                    value={colors.accent}
                    onChange={(e) => handleColorChange('accent', e.target.value)}
                    style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                  />
                  <Input
                    fluid
                    value={colors.accent}
                    onChange={(e) => handleColorChange('accent', e.target.value)}
                    placeholder='#f2711c'
                  />
                </div>
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label htmlFor="branding-color-text">Text Color</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    id="branding-color-text"
                    type='color'
                    value={colors.text}
                    onChange={(e) => handleColorChange('text', e.target.value)}
                    style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                  />
                  <Input
                    fluid
                    value={colors.text}
                    onChange={(e) => handleColorChange('text', e.target.value)}
                    placeholder='#333333'
                  />
                </div>
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Form.Field>
                <label htmlFor="branding-color-background">Background Color</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    id="branding-color-background"
                    type='color'
                    value={colors.background}
                    onChange={(e) => handleColorChange('background', e.target.value)}
                    style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                  />
                  <Input
                    fluid
                    value={colors.background}
                    onChange={(e) => handleColorChange('background', e.target.value)}
                    placeholder='#ffffff'
                  />
                </div>
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      ) : (
        <Form.Field>
          <label htmlFor="branding-json">
            Branding Configuration (JSON)
            <Button
              size='mini'
              floated='right'
              type='button'
              onClick={() => setShowDefaultBranding(!showDefaultBranding)}
            >
              <Icon name={showDefaultBranding ? 'eye slash' : 'eye'} />
              {showDefaultBranding ? 'Hide' : 'View'} Default Branding
            </Button>
          </label>
          <TextArea
            id="branding-json"
            name='branding'
            value={brandingJson}
            onChange={(e) => handleBrandingChange(e.target.value)}
            placeholder='Enter branding configuration as JSON (colors, typography, spacing, etc.)'
            rows={12}
            style={{ fontFamily: 'monospace', fontSize: '13px' }}
          />
          {jsonError && (
            <Label pointing color='red'>
              {jsonError}
            </Label>
          )}
          {!jsonError && (
            <Label pointing>
              Configure colors, typography, spacing, and other visual elements as a JSON object.
            </Label>
          )}
          <div style={{ marginTop: '10px' }}>
            <Button size='small' type='button' onClick={handleLoadDefault}>
              <Icon name='download' />
              Load Default Branding
            </Button>
            <Button size='small' type='button' onClick={handleClearBranding}>
              <Icon name='trash' />
              Clear Branding
            </Button>
          </div>
        </Form.Field>
      )}

      {showDefaultBranding && defaultBranding && (
        <Message info>
          <Message.Header>Default Branding Configuration</Message.Header>
          <pre
            style={{
              background: '#f8f8f8',
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px'
            }}
          >
            {JSON.stringify(defaultBranding, null, 2)}
          </pre>
        </Message>
      )}

      {!useColorPicker && (
        <Message info>
          <Message.Header>Branding Configuration Structure</Message.Header>
          <Message.Content>
            <p>The branding object supports the following top-level keys:</p>
            <ul>
              <li>
                <strong>colors</strong>: primary, secondary, accent, text, background, etc.
              </li>
              <li>
                <strong>typography</strong>: fontFamily, fontSize, fontWeight, etc.
              </li>
              <li>
                <strong>spacing</strong>: base, small, medium, large
              </li>
              <li>
                <strong>borderRadius</strong>: small, medium, large
              </li>
              <li>
                <strong>email</strong>: headerBackgroundColor, buttonBackgroundColor,
                footerTextColor
              </li>
            </ul>
            <p style={{ marginTop: '10px' }}>
              You can add any custom properties needed for your white-label implementation.
            </p>
          </Message.Content>
        </Message>
      )}
      </>
      )}
    </>
  );
}

export default BrandingSectionEnhanced;
